/**
 * 桌面公共头：苹果官网式共用下拉。
 * 首次移入面板从上往下展开；在导航项之间来回切换时面板保持打开，仅交叉淡入内容并过渡高度。
 */
const FLYOUT_OPEN_MS = 400;
const FLYOUT_CLOSE_MS = 320;
const FLYOUT_LEAVE_DELAY_MS = 80;
const FLYOUT_DESKTOP_MQ = "(min-width: 1280px) and (pointer: fine)";

class HeaderNavFlyout extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;
    this.header = this.closest("x-header") || this.closest(".header");
    this.panel = this.querySelector(".header__flyout-panel");
    this.overlay = this.querySelector(".header__flyout-overlay");
    this.flyout = this.querySelector(".header__flyout");
    this.current = null;
    this.isOpen = false;
    this._closeTimer = 0;
    this._readyTimer = 0;
    this._finishTimer = 0;
    this.collectMenus();
    this.syncOverlayTop();
    this.bindEvents();
    this.patchDialogs();
  }

  disconnectedCallback() {
    this._bound = false;
    this.teardownEvents();
    clearTimeout(this._closeTimer);
    clearTimeout(this._readyTimer);
    clearTimeout(this._finishTimer);
  }

  collectMenus() {
    if (!this.header || !this.panel) return;
    this.header.querySelectorAll(".header__primary-nav .header__nav-link[aria-controls]").forEach((link) => {
      const menu = document.getElementById(link.getAttribute("aria-controls"));
      if (menu && (menu.matches("header-mega-menu") || menu.matches("header-dropdown-menu")) && menu.parentElement !== this.panel) {
        this.panel.append(menu);
        menu.style.removeProperty("display");
      }
    });
  }

  bindEvents() {
    this._onPointerOver = this.onPointerOver.bind(this);
    this._onHeaderLeave = this.onHeaderLeave.bind(this);
    this._onOverlayClick = () => this.close();
    this._onKeydown = (event) => {
      if (event.key === "Escape") this.close();
    };
    this._onResize = () => {
      if (!this.isDesktop()) {
        this.close({ instant: true });
        return;
      }
      if (this.isOpen && this.current) {
        this.syncOverlayTop();
        this.setHeight(this.measure(this.current));
      }
    };
    this._onTriggerClick = this.onTriggerClick.bind(this);

    this.header?.addEventListener("pointerover", this._onPointerOver);
    this.header?.addEventListener("pointerleave", this._onHeaderLeave);
    this._onScroll = () => {
      if (this.isOpen) this.syncOverlayTop();
    };
    this.overlay?.addEventListener("click", this._onOverlayClick);
    document.addEventListener("keydown", this._onKeydown);
    document.addEventListener("click", this._onTriggerClick, true);
    window.addEventListener("resize", this._onResize);
    window.addEventListener("scroll", this._onScroll, { passive: true });
  }

  teardownEvents() {
    this.header?.removeEventListener("pointerover", this._onPointerOver);
    this.header?.removeEventListener("pointerleave", this._onHeaderLeave);
    this.overlay?.removeEventListener("click", this._onOverlayClick);
    document.removeEventListener("keydown", this._onKeydown);
    document.removeEventListener("click", this._onTriggerClick, true);
    window.removeEventListener("resize", this._onResize);
    window.removeEventListener("scroll", this._onScroll);
  }

  /**
   * 一级菜单设置 [open] 时 DialogElement 会回调 show/hide，这里只接管面板内的一级菜单，
   * 嵌套下拉仍走原逻辑。
   */
  async patchDialogs() {
    await Promise.all(["header-mega-menu", "header-dropdown-menu"].map((tag) => customElements.whenDefined(tag)));
    const patch = (tag) => {
      const Ctor = customElements.get(tag);
      if (!Ctor || Ctor.prototype.__headerFlyoutPatched) return;
      Ctor.prototype.__headerFlyoutPatched = true;
      const origShow = Ctor.prototype.show;
      const origHide = Ctor.prototype.hide;
      Ctor.prototype.show = function showWithFlyout(opts) {
        const flyout = this.closest("header-nav-flyout");
        if (flyout && this.parentElement === flyout.panel) {
          return flyout.openMenu(this.id);
        }
        return origShow.call(this, opts);
      };
      Ctor.prototype.hide = function hideWithFlyout() {
        const flyout = this.closest("header-nav-flyout");
        if (!flyout || this.parentElement !== flyout.panel) {
          return origHide.call(this);
        }
        if (flyout.current === this) return flyout.close();
        flyout.deactivate(this);
        return Promise.resolve();
      };
    };
    patch("header-mega-menu");
    patch("header-dropdown-menu");
  }

  isDesktop() {
    return window.matchMedia("(min-width: 1280px)").matches;
  }

  isDesktopHover() {
    return window.matchMedia(FLYOUT_DESKTOP_MQ).matches;
  }

  getMenu(id) {
    if (!id || !this.panel) return null;
    try {
      return this.panel.querySelector(`#${CSS.escape(id)}`);
    } catch {
      return document.getElementById(id);
    }
  }

  onTriggerClick(event) {
    if (!this.isDesktop()) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest(".header__primary-nav .header__nav-link[aria-controls]");
    if (!trigger || !this.header.contains(trigger)) return;
    const menu = this.getMenu(trigger.getAttribute("aria-controls"));
    if (!menu) return;
    if (this.isOpen && this.current === menu) {
      event.stopPropagation();
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.openMenu(menu.id);
  }

  onPointerOver(event) {
    if (!this.isDesktopHover()) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest(".header__flyout-overlay")) {
      this.scheduleClose();
      return;
    }

    if (this.flyout?.contains(target)) {
      this.cancelClose();
      return;
    }

    const item = target.closest(".header__primary-nav li");
    if (item && this.header.contains(item)) {
      const trigger = item.querySelector(":scope > .header__nav-link[aria-controls]");
      const menu = trigger ? this.getMenu(trigger.getAttribute("aria-controls")) : null;
      if (menu) {
        this.openMenu(menu.id);
        return;
      }
      this.scheduleClose();
      return;
    }

    if (target.closest(".header__primary-nav")) {
      this.cancelClose();
      return;
    }

    if (target.closest(".header__logo, .header__secondary-nav")) {
      this.scheduleClose();
      return;
    }

    if (this.header.contains(target)) {
      this.cancelClose();
    }
  }

  onHeaderLeave(event) {
    if (!this.isDesktopHover()) return;
    const next = event.relatedTarget;
    if (next instanceof Element && this.contains(next)) {
      this.cancelClose();
      return;
    }
    this.scheduleClose();
  }

  scheduleClose() {
    this.cancelClose();
    this._closeTimer = window.setTimeout(() => this.close(), FLYOUT_LEAVE_DELAY_MS);
  }

  cancelClose() {
    clearTimeout(this._closeTimer);
    this._closeTimer = 0;
  }

  openMenu(id) {
    const menu = this.getMenu(id);
    if (!menu) return Promise.resolve();
    this.cancelClose();
    if (this.current === menu && this.isOpen) return Promise.resolve();
    if (this.isOpen) return this.switchTo(menu);
    return this.open(menu);
  }

  open(menu) {
    this.isOpen = true;
    this.current = menu;
    this.syncOverlayTop();
    this.header.classList.remove("is-flyout-switching", "is-flyout-ready");
    this.header.classList.add("is-flyout-open");
    this.activate(menu);
    this.setHeight(this.measure(menu));
    this.overlay?.setAttribute("aria-hidden", "false");
    this.flyout?.setAttribute("aria-hidden", "false");
    clearTimeout(this._readyTimer);
    this._readyTimer = window.setTimeout(() => {
      if (this.isOpen) this.header.classList.add("is-flyout-ready");
    }, this.reducedMotion() ? 0 : FLYOUT_OPEN_MS);
    return Promise.resolve();
  }

  switchTo(menu) {
    const previous = this.current;
    this.current = menu;
    this.header.classList.add("is-flyout-switching", "is-flyout-ready", "is-flyout-open");
    this.activate(menu);
    if (previous && previous !== menu) this.deactivate(previous);
    this.setHeight(this.measure(menu));
    return Promise.resolve();
  }

  close({ instant = false } = {}) {
    this.cancelClose();
    if (!this.isOpen && !this.header?.classList.contains("is-flyout-open")) {
      return Promise.resolve();
    }
    this.isOpen = false;
    clearTimeout(this._readyTimer);
    this.header.classList.remove("is-flyout-open", "is-flyout-ready", "is-flyout-switching");
    this.overlay?.setAttribute("aria-hidden", "true");
    this.flyout?.setAttribute("aria-hidden", "true");

    const finish = () => {
      if (this.isOpen) return;
      this.deactivate(this.current);
      this.current = null;
      if (this.flyout) this.flyout.style.height = "";
    };

    if (instant || this.reducedMotion()) {
      finish();
      return Promise.resolve();
    }

    clearTimeout(this._finishTimer);
    this._finishTimer = window.setTimeout(finish, FLYOUT_CLOSE_MS);
    return Promise.resolve();
  }

  activate(menu) {
    if (!menu) return;
    menu.style.removeProperty("display");
    menu.classList.add("is-active");
    menu.setAttribute("open", "");
    menu.setAttribute("aria-hidden", "false");
    this.controlsOf(menu).forEach((control) => control.setAttribute("aria-expanded", "true"));
  }

  deactivate(menu) {
    if (!menu) return;
    menu.classList.remove("is-active");
    menu.removeAttribute("open");
    menu.setAttribute("aria-hidden", "true");
    this.controlsOf(menu).forEach((control) => control.setAttribute("aria-expanded", "false"));
  }

  controlsOf(menu) {
    if (!menu?.id) return [];
    return Array.from(this.header.querySelectorAll(`[aria-controls="${menu.id}"]`));
  }

  measure(menu) {
    if (!menu) return 0;
    const headerBottom = (this.header.closest(".shopify-section--header") || this.header).getBoundingClientRect().bottom;
    const maxHeight = Math.max(120, window.innerHeight - headerBottom - 24);
    menu.style.maxHeight = "none";
    const contentHeight = menu.scrollHeight;
    menu.style.maxHeight = `${maxHeight}px`;
    return Math.min(contentHeight, maxHeight);
  }

  setHeight(px) {
    if (this.flyout) this.flyout.style.height = `${Math.round(px)}px`;
  }

  syncOverlayTop() {
    const bar = this.header || this.closest(".shopify-section--header");
    if (!bar) return;
    const top = `${Math.floor(bar.getBoundingClientRect().bottom) - 1}px`;
    if (this.overlay) this.overlay.style.top = top;
    if (this.flyout) this.flyout.style.top = top;
  }

  reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }
}

if (!window.customElements.get("header-nav-flyout")) {
  window.customElements.define("header-nav-flyout", HeaderNavFlyout);
}
